import { CloseOutlined } from '@ant-design/icons'
import { renderReportHeatmapBundle } from '@asset-viz/reporting/reportHeatmap'
import { Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd'
import {
  CommercialFdeCdfSpecificationList,
  getCdfDisplayMetadata,
  useCdfDataFetcher,
  useInitializeDerivedCdfsFromRolodexData,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useAxisNames } from 'features/locale'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'features/reporting/types'
import sortBy from 'lodash/sortBy'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import { PdfHeatmapWithLegend } from 'pages/reporting/reports/rug/components/PdfHeatmapWithLegend'
import {
  LAYOUT,
  MATH_CONSTANTS,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { useSelectableComponentCDFs } from 'pages/reporting/reports/rug/hooks/useSelectableComponentCDFs'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ComponentId,
  RugComponentStats,
} from 'pages/reporting/reports/rug/types'
import { hasSectionData } from 'pages/reporting/reports/rug/utils/calculateRUGStatsForSection'
import { getBoundsFromSectionable } from 'pages/reporting/reports/rug/utils/getBoundsFromSectionable'
import { getRugDataImagePageStats } from 'pages/reporting/reports/rug/utils/getRugDataImagePageStats'
import { renderReportHeatmapSlices } from 'pages/reporting/reports/rug/utils/renderReportHeatmapSlices'
import { getSectionHeatmapOverlayOptions } from 'pages/reporting/reports/rug/utils/reportHeatmapOverlayOptions'
import type { ReportHeatmapSliceAxis } from 'pages/reporting/reports/rug/utils/reportHeatmapSlices'
import {
  buildReportHeatmapBundlePaths,
  createReportHeatmapSectionKey,
  findSectionByKey,
  getBundleFilePaths,
  isFullComponentSectionKey,
  serializeReportHeatmapDescriptor,
} from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModelType,
  GeckoComponentDataFrame,
  GeckoComponentModelType,
  GeckoComponentSpace,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  isInspectionThicknessCDF,
  Section,
} from 'types'

const { Text: AntText } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ComponentSelector = styled.div`
  margin-bottom: 1rem;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const HeatmapHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  h4 {
    margin: 0;
  }
`

const HeatmapPill = styled.div`
  display: flex;
  align-items: stretch;
`

const HeatmapPillSelectButton = styled(Button)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`

const HeatmapPillDeleteButton = styled(Button)`
  min-width: 2.25rem;
  padding-inline: 0.5rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`

const ImageCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-image {
    background-color: #ffffff;
  }
`

interface SectionScreenshot {
  componentId: string
  componentName: string
  sectionKey: string
  sectionName: string
  sortOrder: number
  signedUrl: string
  heatmapPath: string
  imagePath: string
  legendSignedUrl?: string
  legendPath?: string
  descriptorPath?: string
  bundlePath: string
  stats?: RugComponentStats
}

const getSectionHeatmapCommentKey = (componentId: string, sectionKey: string) =>
  `heatmap:${componentId}:${sectionKey}`

const PDF_TEXT_WIDTH_PER_CHAR = 5.2
const PDF_CELL_HORIZONTAL_PADDING = SPACING.PADDING.EXTRA_SMALL * 2 + 8
const SECTION_PDF_FOOTER_PADDING = 5
const SECTION_PDF_HEATMAP_HEIGHT = 340
const SECTION_PDF_HEATMAP_MAX_WIDTH = 640
const SECTION_PDF_LEGEND_WIDTH = 56
const SECTION_PDF_IMAGE_GAP = 6

type MeasurementColumnWidths = {
  label: number
  value: number
  locationX: number
  locationY: number
  total: number
}

export const getMeasurementAxisLabels = (
  assetModelType?: GeckoAssetModelType | string,
  componentType?: GeckoComponentModelType | string
): { x: string; y: string } => {
  switch (componentType) {
    case GeckoComponentModelType.TankFloor:
    case GeckoComponentModelType.TankFlatRoof:
      return { x: 'Location-X', y: 'Location-Y' }
    case GeckoComponentModelType.LegacyCone:
    case GeckoComponentModelType.Cone:
      return { x: 'Angle', y: 'Radius' }
  }

  switch (assetModelType) {
    case GeckoAssetModelType.LegacyBoiler:
      return { x: 'Tubes', y: 'Elevation' }
    case GeckoAssetModelType.VerticalTank:
    case GeckoAssetModelType.HorizontalTank:
      return { x: 'Circumferential Position', y: 'Elevation' }
    case GeckoAssetModelType.Piping:
      return { x: 'Axial Position', y: 'Circumferential Position' }
    default:
      return { x: 'Location-X', y: 'Location-Y' }
  }
}

const tableStyles = {
  container: {
    marginTop: SPACING.MARGIN.TINY,
    flexDirection: 'column' as const,
    gap: 0,
  },
  measurementsColumn: {
    flex: LAYOUT.FLEX.NARROW,
  },
  tableRow: {
    flexDirection: 'row' as const,
    borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCell: {
    padding: SPACING.PADDING.EXTRA_SMALL,
    borderRight: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCellWide: {
    flex: LAYOUT.FLEX.WIDE,
  },
  tableCellNarrow: {
    flex: LAYOUT.FLEX.NARROW,
  },
  tableCellLast: {
    borderRight: 'none',
  },
  headerText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
    color: REPORT_COLORS.HEADER_TEXT,
  },
  cellText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
  },
}

const pageStyles = {
  imagePage: {
    flexDirection: 'column' as const,
    padding: SPACING.PADDING.MEDIUM,
    paddingBottom: SPACING.PADDING.LARGE,
    fontSize: TYPOGRAPHY.FONT_SIZE.MEDIUM,
  },
}

function formatLocation(location?: string): string | undefined {
  return location?.replace(/″/g, '"')?.replace(/′/g, "'")
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function estimatePdfColumnWidth(
  values: Array<string | undefined>,
  minWidth: number,
  maxWidth: number
) {
  const longestValueLength = Math.max(
    ...values.map((value) => value?.length ?? 0),
    0
  )

  return clampNumber(
    longestValueLength * PDF_TEXT_WIDTH_PER_CHAR + PDF_CELL_HORIZONTAL_PADDING,
    minWidth,
    maxWidth
  )
}

const TableRow: React.FC<{
  columnWidths: MeasurementColumnWidths
  label: string
  value: string | undefined
  locationX?: string
  locationY?: string
  isLastRow?: boolean
}> = ({
  columnWidths,
  label,
  value,
  locationX,
  locationY,
  isLastRow = false,
}) => (
  <View
    style={[tableStyles.tableRow, isLastRow ? { borderBottom: 'none' } : {}]}>
    <View style={[tableStyles.tableCell, { width: columnWidths.label }]}>
      <Text style={[tableStyles.cellText, { color: REPORT_COLORS.VALUE_TEXT }]}>
        {label}
      </Text>
    </View>
    <View style={[tableStyles.tableCell, { width: columnWidths.value }]}>
      <Text style={[tableStyles.cellText, { color: REPORT_COLORS.VALUE_TEXT }]}>
        {value}
      </Text>
    </View>
    <View style={[tableStyles.tableCell, { width: columnWidths.locationX }]}>
      <Text style={[tableStyles.cellText, { color: REPORT_COLORS.VALUE_TEXT }]}>
        {locationX}
      </Text>
    </View>
    <View
      style={[
        tableStyles.tableCell,
        { width: columnWidths.locationY },
        tableStyles.tableCellLast,
      ]}>
      <Text style={[tableStyles.cellText, { color: REPORT_COLORS.VALUE_TEXT }]}>
        {locationY}
      </Text>
    </View>
  </View>
)

const SectionStatsTable: React.FC<{
  sectionStats?: RugComponentStats
  nominalThickness?: GeckoMeasurement
  relevantCDF?: GeckoComponentDataFrame
  comment?: string
  axisLabels: { x: string; y: string }
}> = ({ sectionStats, nominalThickness, relevantCDF, comment, axisLabels }) => {
  const { getPreferredMeasurementUnit, formatMeasurement } = useRugReportOps()

  const { userFacingThicknessUnit, userFacingXUnit, userFacingYUnit } =
    useMemo(() => {
      const thicknessUnit = relevantCDF?.data.params.measurement_unit
      const xUnit = relevantCDF?.data.geometry.x.unit
      const yUnit = relevantCDF?.data.geometry.y.unit

      return {
        userFacingThicknessUnit: thicknessUnit
          ? getPreferredMeasurementUnit(thicknessUnit)
          : undefined,
        userFacingXUnit: xUnit ? getPreferredMeasurementUnit(xUnit) : undefined,
        userFacingYUnit: yUnit ? getPreferredMeasurementUnit(yUnit) : undefined,
      }
    }, [getPreferredMeasurementUnit, relevantCDF])

  const { minThicknesses, averageThickness } = useMemo(() => {
    const stats = sectionStats ?? {
      minThicknesses: [],
      averageThickness: GeckoMeasurement.CreateFromValue(
        Number.NaN,
        userFacingThicknessUnit ?? GeckoMeasurementUnit.None
      ),
    }

    return {
      minThicknesses: sortBy(
        stats.minThicknesses.map((minThickness) => ({
          thickness: minThickness.thickness.convertTo(userFacingThicknessUnit),
          location:
            userFacingXUnit && userFacingYUnit
              ? minThickness.location.project(
                  GeckoComponentSpace.CreateFromMeasurementUnits(
                    minThickness.location.componentID,
                    userFacingXUnit,
                    userFacingYUnit
                  )
                )
              : minThickness.location,
        })),
        (thickness) =>
          thickness.thickness.toNumber(
            userFacingThicknessUnit ?? thickness.thickness.unit
          )
      ),
      averageThickness: stats.averageThickness.convertTo(
        userFacingThicknessUnit
      ),
    }
  }, [sectionStats, userFacingThicknessUnit, userFacingXUnit, userFacingYUnit])

  const sectionNominalThickness = nominalThickness?.convertTo(
    userFacingThicknessUnit
  )

  const maxWallLossMeasurement = sectionStats?.maxWallLossPercent

  const maxWallLossDisplay = maxWallLossMeasurement
    ? (formatMeasurement({
        measurement: maxWallLossMeasurement,
        variant: 'narrow',
        minimumFractionDigits: MATH_CONSTANTS.FIXED_DECIMAL_PLACES,
        maximumFractionDigits: MATH_CONSTANTS.FIXED_DECIMAL_PLACES,
      }) ?? 'N/A')
    : 'N/A'

  const nominalThicknessDisplay = formatMeasurement({
    measurement: sectionNominalThickness,
    variant: 'narrow',
  })

  const averageThicknessDisplay = formatMeasurement({
    measurement: averageThickness,
    variant: 'narrow',
  })

  const measurementRows = useMemo(
    () => [
      {
        label: 'Nominal Thickness',
        value: nominalThicknessDisplay,
        locationX: axisLabels.x,
        locationY: axisLabels.y,
      },
      ...minThicknesses.map((minThickness, index) => ({
        label: `Minimum Thickness ${index + 1}`,
        value: formatMeasurement({
          measurement: minThickness.thickness,
          variant: 'narrow',
        }),
        locationX: formatLocation(
          formatMeasurement({
            measurement: minThickness.location.x,
            variant: 'geometric',
          })
        ),
        locationY: formatLocation(
          formatMeasurement({
            measurement: minThickness.location.y,
            variant: 'geometric',
          })
        ),
      })),
      {
        label: 'Max Wall Loss',
        value: maxWallLossDisplay,
        locationX: undefined,
        locationY: undefined,
      },
      {
        label: 'Average Thickness',
        value: averageThicknessDisplay,
        locationX: undefined,
        locationY: undefined,
      },
    ],
    [
      averageThicknessDisplay,
      formatMeasurement,
      maxWallLossDisplay,
      minThicknesses,
      nominalThicknessDisplay,
      axisLabels.x,
      axisLabels.y,
    ]
  )

  const columnWidths = useMemo<MeasurementColumnWidths>(() => {
    const labelWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.label),
      118,
      170
    )
    const valueWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.value),
      70,
      104
    )
    const locationXWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.locationX).concat(axisLabels.x),
      76,
      104
    )
    const locationYWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.locationY).concat(axisLabels.y),
      68,
      96
    )

    return {
      label: labelWidth,
      value: valueWidth,
      locationX: locationXWidth,
      locationY: locationYWidth,
      total: labelWidth + valueWidth + locationXWidth + locationYWidth,
    }
  }, [axisLabels.x, axisLabels.y, measurementRows])

  const totalDataRows = measurementRows.length
  const commentText = comment?.trim()

  return (
    <View
      wrap={false}
      style={[
        tableStyles.container,
        {
          maxHeight: 118,
          overflow: 'hidden',
        },
      ]}>
      <View style={tableStyles.tableRow}>
        <View
          style={[
            tableStyles.tableCell,
            {
              width: columnWidths.total,
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text style={[tableStyles.headerText]}>Measurements</Text>
        </View>
        <View
          style={[
            tableStyles.tableCell,
            tableStyles.tableCellLast,
            {
              flex: 1,
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text style={tableStyles.headerText}>Comments</Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row' }}>
        <View style={{ width: columnWidths.total }}>
          {measurementRows.map((row, index) => (
            <TableRow
              key={`${row.label}-${index}`}
              columnWidths={columnWidths}
              label={row.label}
              value={row.value}
              locationX={row.locationX}
              locationY={row.locationY}
              isLastRow={index === measurementRows.length - 1}
            />
          ))}
        </View>
        <View
          style={{
            flex: 1,
            borderLeft: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            minHeight: totalDataRows * 16,
            maxHeight: 96,
            overflow: 'hidden',
            padding: SPACING.PADDING.EXTRA_SMALL,
          }}>
          {commentText ? (
            <Text
              style={{
                fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
                lineHeight: TYPOGRAPHY.LINE_HEIGHT.NORMAL,
                color: REPORT_COLORS.DARKER_GRAY,
              }}>
              {commentText}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  )
}

const RenderReact: FC = () => {
  const {
    formData,
    handleAssetDrawingsFinish,
    selectedComponents,
    relevantCDFs,
    metadataEntryId,
    selectedComponentModels,
    assetScreenshots,
    sectionsByComponent,
    forceRefreshAssetScreenshots,
    registerGeneratedHeatmapBundle,
    unit,
    cdfCache,
    componentDefaults,
    componentDatasets,
    selectedAssetModel,
    inspections,
    statsBySection,
    statsByComponent,
    getPreferredMeasurementUnit,
  } = useRugReportOps()

  const [selectedComponentId, setSelectedComponentId] = useState<
    ComponentId | undefined
  >()
  const [selectedCDFId, setSelectedCDFId] = useState<string | undefined>()
  const [capturingSection, setCapturingSection] = useState(false)
  const [currentSectionIdx, setCurrentSectionIdx] = useState<number | null>(
    null
  )
  const [captureTargetCount, setCaptureTargetCount] = useState(0)
  const [pendingGeneratedHeatmaps, setPendingGeneratedHeatmaps] = useState<{
    keys: string[]
    startedAt: number
  }>()
  const [deletingImageKey, setDeletingImageKey] = useState<string | null>(null)
  const [deletingAllForComponent, setDeletingAllForComponent] = useState(false)
  const [editedComments, setEditedComments] = useState<Record<string, string>>(
    {}
  )
  const [selectedHeatmapKey, setSelectedHeatmapKey] = useState<string>()

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()

  const selectedComponent = useMemo(() => {
    if (!selectedComponentId || !selectedComponents) return undefined
    return selectedComponents.find(
      (component) => component.id === selectedComponentId
    )
  }, [selectedComponentId, selectedComponents])

  const { sections, sectionable } = useMemo(() => {
    if (!selectedComponent) {
      return {
        sections: [] as Section[],
        sectionable: undefined,
      }
    }

    const componentSections = sectionsByComponent.get(
      selectedComponent.id as ComponentId
    )

    return {
      sections: componentSections?.sections ?? [],
      sectionable: componentSections,
    }
  }, [selectedComponent, sectionsByComponent])

  const hasSections = sections.length > 0

  const componentCDFs = useSelectableComponentCDFs({
    selectedComponent,
    relevantCDFs,
    componentDatasets,
    selectedAssetModel,
    inspections,
  })

  const relevantCDF = useMemo(() => {
    if (!selectedComponent || componentCDFs.length === 0) return undefined
    if (selectedCDFId) {
      return componentCDFs.find((cdf) => cdf.id === selectedCDFId)
    }
    return componentCDFs[0]
  }, [componentCDFs, selectedCDFId, selectedComponent])

  const selectedComponentModel = useMemo(
    () =>
      selectedComponentModels?.find(
        (componentModel) => componentModel.id === selectedComponent?.id
      ),
    [selectedComponent?.id, selectedComponentModels]
  )

  const { xAxisName, yAxisName } = useAxisNames(
    relevantCDF?.data.geometry,
    selectedComponentModel?.type
  )

  const axisMetadata = useMemo(() => {
    const geometry = relevantCDF?.data.geometry
    if (!geometry?.x || !geometry.y) return undefined

    return relevantCDF
      ? {
          x: {
            name: xAxisName,
            displayUnit: getPreferredMeasurementUnit(
              relevantCDF.data.geometry.x.unit
            ),
          },
          y: {
            name: yAxisName,
            displayUnit: getPreferredMeasurementUnit(
              relevantCDF.data.geometry.y.unit
            ),
          },
        }
      : undefined
  }, [getPreferredMeasurementUnit, relevantCDF, xAxisName, yAxisName])

  useEffect(() => {
    if (selectedComponent && componentCDFs.length > 0) {
      if (
        !selectedCDFId ||
        !componentCDFs.find((cdf) => cdf.id === selectedCDFId)
      ) {
        setSelectedCDFId(componentCDFs[0].id)
      }
      return
    }

    setSelectedCDFId(undefined)
  }, [componentCDFs, selectedCDFId, selectedComponent])

  const colorThreshold = useMemo(() => {
    if (!relevantCDF) return undefined

    const selectedComponentDefault = componentDefaults.find(
      (componentDefault) =>
        componentDefault.tags.component === selectedComponent?.id
    )

    return selectedComponentDefault?.data.color_scale_thresholds.get(
      relevantCDF.data.params.view.id
    )
  }, [componentDefaults, relevantCDF, selectedComponent?.id])

  const relevantCdfLegendTitle = useMemo(() => {
    if (!relevantCDF) return undefined

    return getCdfDisplayMetadata(
      relevantCDF,
      getPreferredMeasurementUnit(relevantCDF.data.params.measurement_unit)
    ).label
  }, [getPreferredMeasurementUnit, relevantCDF])

  const existingScreenshots = useMemo(() => {
    const screenshots: SectionScreenshot[] = []

    assetScreenshots?.sectionHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      const componentSections = sectionsByComponent.get(
        screenshot.componentId as ComponentId
      )
      const section = findSectionByKey(componentSections, screenshot.sectionKey)
      const sortOrder = section
        ? (componentSections?.sections.findIndex(
            (candidate) => candidate.name === section.name
          ) ?? -1)
        : -1

      screenshots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        sectionKey:
          screenshot.sectionKey ?? createReportHeatmapSectionKey(undefined),
        sectionName:
          section?.name ??
          (isFullComponentSectionKey(screenshot.sectionKey)
            ? component.data.name
            : (screenshot.sectionKey ?? component.data.name)),
        sortOrder,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
      })
    })

    return sortBy(screenshots, ['componentName', 'sortOrder', 'sectionName'])
  }, [assetScreenshots, sectionsByComponent, selectedComponents])

  useEffect(() => {
    if (existingScreenshots.length === 0) {
      setSelectedHeatmapKey(undefined)
      return
    }

    const selectedScreenshotExists = existingScreenshots.some(
      (screenshot) =>
        `${screenshot.componentId}-${screenshot.sectionKey}` ===
        selectedHeatmapKey
    )

    if (!selectedScreenshotExists) {
      const firstScreenshot = existingScreenshots[0]
      setSelectedHeatmapKey(
        `${firstScreenshot.componentId}-${firstScreenshot.sectionKey}`
      )
    }
  }, [existingScreenshots, selectedHeatmapKey])

  const selectedScreenshot = useMemo(() => {
    return existingScreenshots.find(
      (screenshot) =>
        `${screenshot.componentId}-${screenshot.sectionKey}` ===
        selectedHeatmapKey
    )
  }, [existingScreenshots, selectedHeatmapKey])

  const waitingForGeneratedHeatmaps =
    (pendingGeneratedHeatmaps?.keys.length ?? 0) > 0

  useEffect(() => {
    if (!pendingGeneratedHeatmaps || pendingGeneratedHeatmaps.keys.length === 0)
      return

    const existingHeatmapKeys = new Set(
      existingScreenshots.map(
        (screenshot) => `${screenshot.componentId}-${screenshot.sectionKey}`
      )
    )
    const generatedHeatmapsAreVisible = pendingGeneratedHeatmaps.keys.every(
      (heatmapKey) => existingHeatmapKeys.has(heatmapKey)
    )
    if (!generatedHeatmapsAreVisible) return

    setPendingGeneratedHeatmaps(undefined)
    message.success('Heatmaps generated successfully')
  }, [existingScreenshots, pendingGeneratedHeatmaps])

  useEffect(() => {
    setEditedComments((currentComments) => {
      const nextComments = { ...currentComments }

      existingScreenshots.forEach((screenshot) => {
        const commentKey = getSectionHeatmapCommentKey(
          screenshot.componentId,
          screenshot.sectionKey
        )
        nextComments[commentKey] =
          formData.assetDrawings.images[commentKey]?.imageCaption ?? ''
      })

      return nextComments
    })
  }, [existingScreenshots, formData.assetDrawings.images])

  const deleteScreenshotBundle = useCallback(
    async (screenshot: SectionScreenshot) => {
      for (const path of getBundleFilePaths(screenshot)) {
        await rolodexDeleteFile(metadataEntryId, path)
      }
    },
    [metadataEntryId, rolodexDeleteFile]
  )

  const handleDeleteScreenshot = useCallback(
    async (screenshot: SectionScreenshot) => {
      if (deletingAllForComponent || deletingImageKey) return

      const screenshotKey = `${screenshot.componentId}-${screenshot.sectionKey}`
      setDeletingImageKey(screenshotKey)
      try {
        await deleteScreenshotBundle(screenshot)
        await forceRefreshAssetScreenshots()
        message.success('Heatmap deleted successfully')
      } catch (error) {
        console.error('Failed to delete section heatmap bundle:', error)
        message.error('Failed to delete heatmap')
        try {
          await forceRefreshAssetScreenshots()
        } catch {
          return
        }
      } finally {
        setDeletingImageKey(null)
      }
    },
    [
      deleteScreenshotBundle,
      deletingAllForComponent,
      deletingImageKey,
      forceRefreshAssetScreenshots,
    ]
  )

  const handleDeleteAllScreenshots = useCallback(async () => {
    if (
      existingScreenshots.length === 0 ||
      deletingAllForComponent ||
      deletingImageKey
    ) {
      return
    }

    const screenshotCount = existingScreenshots.length
    setDeletingAllForComponent(true)
    try {
      for (const screenshot of existingScreenshots) {
        await deleteScreenshotBundle(screenshot)
      }
      await forceRefreshAssetScreenshots()
      message.success(`${screenshotCount} heatmaps deleted successfully`)
    } catch {
      message.error('Failed to delete heatmaps')
      try {
        await forceRefreshAssetScreenshots()
      } catch {
        return
      }
    } finally {
      setDeletingAllForComponent(false)
    }
  }, [
    deletingAllForComponent,
    deleteScreenshotBundle,
    deletingImageKey,
    forceRefreshAssetScreenshots,
    existingScreenshots,
  ])

  const deletionControlsDisabled =
    deletingImageKey !== null || deletingAllForComponent

  const handleCommentChange = useCallback(
    (commentKey: string, value: string) => {
      setEditedComments((currentComments) => ({
        ...currentComments,
        [commentKey]: value,
      }))
    },
    []
  )

  const handleCommentSave = useCallback(
    async (screenshot: SectionScreenshot) => {
      const commentKey = getSectionHeatmapCommentKey(
        screenshot.componentId,
        screenshot.sectionKey
      )
      await handleAssetDrawingsFinish(
        screenshot.imagePath,
        commentKey,
        screenshot.sortOrder,
        editedComments[commentKey]?.trim() || undefined
      )
    },
    [editedComments, handleAssetDrawingsFinish]
  )

  const cdfDataFetcher = useCdfDataFetcher()
  const startCapture = useCallback(async () => {
    if (!selectedComponent || !relevantCDF) {
      message.error('Unable to generate heatmaps - missing required data')
      return
    }

    if (!selectedComponentModel) {
      message.error('Unable to generate heatmaps - missing component model')
      return
    }

    setCapturingSection(true)
    try {
      const buffer = await cdfDataFetcher(relevantCDF)
      if (!buffer) {
        message.error('Unable to generate heatmaps - missing CDF data')
        return
      }

      const captureTargets = hasSections
        ? getBoundsFromSectionable(
            selectedComponent.id as ComponentId,
            sectionable!,
            relevantCDF.data.geometry
          )
            .map((bounds, index) => ({
              sectionKey: createReportHeatmapSectionKey(sections[index]?.name),
              section: sections[index],
              bounds,
            }))
            .filter(({ section, sectionKey }) =>
              hasSectionData(
                getRugDataImagePageStats({
                  section,
                  sectionKey,
                  sectionable,
                  componentId: selectedComponent.id as ComponentId,
                  statsBySection,
                  statsByComponent,
                }).stats
              )
            )
        : [
            {
              sectionKey: createReportHeatmapSectionKey(undefined),
              bounds: selectedComponentModel.componentBounds,
            },
          ]

      setCaptureTargetCount(captureTargets.length)

      const existingBundles =
        assetScreenshots?.sectionHeatmap?.filter(
          (screenshot) => screenshot.componentId === selectedComponent.id
        ) ?? []
      for (const existingBundle of existingBundles) {
        for (const path of getBundleFilePaths(existingBundle)) {
          await rolodexDeleteFile(metadataEntryId, path)
        }
      }

      if (captureTargets.length === 0) {
        message.info('Unable to generate heatmaps - no section data available')
        return
      }

      const sectionHeatmapSplitAxis: ReportHeatmapSliceAxis | undefined =
        sectionable?.splitByX ? 'y' : undefined
      const sectionHeatmapSliceOptions = {
        axis: sectionHeatmapSplitAxis,
        sharedSliceBounds: captureTargets.map(({ bounds }) => bounds),
      }

      for (const [index, target] of captureTargets.entries()) {
        setCurrentSectionIdx(index)

        const renderBundle = hasSections
          ? (request: Parameters<typeof renderReportHeatmapSlices>[0]) =>
              renderReportHeatmapSlices(request, sectionHeatmapSliceOptions)
          : renderReportHeatmapBundle
        const bundle = await renderBundle({
          kind: 'section',
          componentId: selectedComponent.id as ComponentId,
          sectionKey: target.sectionKey,
          cdfId: relevantCDF.id,
          legendTitle: relevantCdfLegendTitle,
          cdf: relevantCDF,
          componentModel: selectedComponentModel,
          bounds: target.bounds,
          colorThreshold,
          valueDisplayUnit: getPreferredMeasurementUnit(
            relevantCDF.data.params.measurement_unit
          ),
          nominalDisplayUnit: getPreferredMeasurementUnit(
            GeckoMeasurementUnit.Inches
          ),
          ...getSectionHeatmapOverlayOptions(),
          includeAxes: true,
          axisMetadata,
          includeLegend: true,
          outputWidthPx: 2000,
          background: 'white',
          rawValues: buffer.values,
        })

        const paths = buildReportHeatmapBundlePaths({
          kind: 'section',
          componentId: selectedComponent.id as ComponentId,
          sectionKey: target.sectionKey,
          descriptorHash: bundle.descriptor.hash,
        })

        await Promise.all([
          rolodexUploadFile(
            metadataEntryId,
            bundle.heatmapBlob,
            paths.heatmapPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            bundle.legendBlob,
            paths.legendPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            new Blob([serializeReportHeatmapDescriptor(bundle.descriptor)], {
              type: 'application/json',
            }),
            paths.descriptorPath
          ),
        ])
        await registerGeneratedHeatmapBundle({
          ...paths,
          heatmapBlob: bundle.heatmapBlob,
          legendBlob: bundle.legendBlob,
        })
      }

      setPendingGeneratedHeatmaps({
        keys: captureTargets.map(
          (target) => `${selectedComponent.id}-${target.sectionKey}`
        ),
        startedAt: performance.now(),
      })
      await forceRefreshAssetScreenshots()
    } catch (error) {
      setPendingGeneratedHeatmaps(undefined)
      console.error('Failed to generate section heatmaps:', error)
      message.error('Failed to generate heatmaps')
    } finally {
      setCapturingSection(false)
      setCurrentSectionIdx(null)
      setCaptureTargetCount(0)
    }
  }, [
    assetScreenshots?.sectionHeatmap,
    cdfDataFetcher,
    colorThreshold,
    getPreferredMeasurementUnit,
    forceRefreshAssetScreenshots,
    registerGeneratedHeatmapBundle,
    hasSections,
    relevantCdfLegendTitle,
    metadataEntryId,
    relevantCDF,
    rolodexDeleteFile,
    rolodexUploadFile,
    sectionable,
    sections,
    selectedComponent,
    selectedComponentModel,
    statsBySection,
    statsByComponent,
    axisMetadata,
  ])

  const componentOptions = useMemo(() => {
    if (!selectedComponents) return []
    return selectedComponents.map((component) => ({
      label: component.data.name,
      value: component.id,
    }))
  }, [selectedComponents])

  useInitializeDerivedCdfsFromRolodexData(
    cdfCache,
    CommercialFdeCdfSpecificationList,
    unit?.id ?? [],
    isInspectionThicknessCDF
  )

  const cdfOptions = componentCDFs.map((cdf) => ({
    label: `${
      getCdfDisplayMetadata(
        cdf,
        getPreferredMeasurementUnit(cdf.data.params.measurement_unit)
      ).label
    }${
      cdf.data.params.aggregation.name
        ? ` (${cdf.data.params.aggregation.name})`
        : ''
    }`,
    value: cdf.id,
  }))

  return (
    <Container>
      <ComponentSelector>
        <Space orientation='vertical' style={{ width: '100%' }}>
          <AntText strong>Select Component:</AntText>
          <Select
            style={{ width: '100%' }}
            placeholder='Select a component'
            value={selectedComponentId}
            onChange={setSelectedComponentId}
            options={componentOptions}
          />
          {selectedComponent && cdfOptions.length > 0 && (
            <>
              <AntText strong>Select CDF:</AntText>
              <Select
                style={{ width: '100%' }}
                placeholder='Select a CDF'
                value={selectedCDFId}
                onChange={setSelectedCDFId}
                options={cdfOptions}
              />
            </>
          )}
          {componentOptions.length === 0 && (
            <Alert message='No components available' type='warning' showIcon />
          )}
        </Space>
      </ComponentSelector>

      {selectedComponent && (
        <Space direction='vertical' size='small'>
          <Button
            type='primary'
            onClick={startCapture}
            loading={capturingSection}
            disabled={!selectedComponent}>
            {capturingSection ? 'Generating Heatmaps...' : 'Generate Heatmaps'}
          </Button>
          {capturingSection && currentSectionIdx !== null && (
            <AntText type='secondary'>
              Rendering {currentSectionIdx + 1} of {captureTargetCount}
            </AntText>
          )}
          {waitingForGeneratedHeatmaps && (
            <Space align='center'>
              <Spin size='small' />
              <AntText type='secondary'>
                Refreshing generated heatmaps...
              </AntText>
            </Space>
          )}
        </Space>
      )}

      <PreviewSection>
        <HeatmapHeading>
          <Typography.Title level={4}>
            Captured Section Heatmaps
          </Typography.Title>
          <Popconfirm
            title='Delete all heatmaps?'
            description={`This will remove ${existingScreenshots.length} heatmaps from the report.`}
            disabled={
              existingScreenshots.length === 0 || deletionControlsDisabled
            }
            okText='Delete'
            cancelText='Cancel'
            okButtonProps={{
              danger: true,
              loading: deletingAllForComponent,
            }}
            onConfirm={handleDeleteAllScreenshots}>
            <Button
              danger
              loading={deletingAllForComponent}
              disabled={
                existingScreenshots.length === 0 || deletionControlsDisabled
              }>
              Delete all
            </Button>
          </Popconfirm>
        </HeatmapHeading>
        {existingScreenshots.length === 0 && !waitingForGeneratedHeatmaps ? (
          <Alert message='No captured heatmaps yet' type='info' showIcon />
        ) : (
          <>
            <Space wrap size='small'>
              {existingScreenshots.map((screenshot) => {
                const screenshotKey = `${screenshot.componentId}-${screenshot.sectionKey}`
                return (
                  <HeatmapPill key={screenshotKey}>
                    <HeatmapPillSelectButton
                      type={
                        selectedHeatmapKey === screenshotKey
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => setSelectedHeatmapKey(screenshotKey)}>
                      {screenshot.componentName}: {screenshot.sectionName}
                    </HeatmapPillSelectButton>
                    <Popconfirm
                      title='Delete heatmap?'
                      description='This will remove the heatmap bundle from the report.'
                      disabled={deletionControlsDisabled}
                      okText='Delete'
                      cancelText='Cancel'
                      okButtonProps={{
                        danger: true,
                        loading: deletingImageKey === screenshotKey,
                      }}
                      onConfirm={() => handleDeleteScreenshot(screenshot)}>
                      <HeatmapPillDeleteButton
                        danger
                        disabled={deletionControlsDisabled}
                        loading={deletingImageKey === screenshotKey}
                        aria-label={`Delete ${screenshot.componentName}: ${screenshot.sectionName}`}
                        icon={<CloseOutlined />}
                      />
                    </Popconfirm>
                  </HeatmapPill>
                )
              })}
            </Space>

            {selectedScreenshot && (
              <ImageCard
                title={`${selectedScreenshot.componentName} - ${selectedScreenshot.sectionName}`}>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    width: '100%',
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <AntImage
                      src={selectedScreenshot.signedUrl}
                      alt={`${selectedScreenshot.componentName} - ${selectedScreenshot.sectionName}`}
                      style={{
                        display: 'block',
                        width: '100%',
                      }}
                    />
                  </div>
                  {selectedScreenshot.legendSignedUrl && (
                    <AntImage
                      src={selectedScreenshot.legendSignedUrl}
                      alt={`${selectedScreenshot.sectionName} legend`}
                      style={{ width: '72px' }}
                    />
                  )}
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <AntText strong>Comments</AntText>
                  <Input.TextArea
                    rows={3}
                    placeholder='Add comments for this section heatmap'
                    value={
                      editedComments[
                        getSectionHeatmapCommentKey(
                          selectedScreenshot.componentId,
                          selectedScreenshot.sectionKey
                        )
                      ] ?? ''
                    }
                    onChange={(event) =>
                      handleCommentChange(
                        getSectionHeatmapCommentKey(
                          selectedScreenshot.componentId,
                          selectedScreenshot.sectionKey
                        ),
                        event.target.value
                      )
                    }
                    style={{ marginTop: '0.5rem' }}
                  />
                  <Button
                    onClick={() => handleCommentSave(selectedScreenshot)}
                    style={{ marginTop: '0.5rem' }}>
                    Save Comments
                  </Button>
                </div>
                <Popconfirm
                  title='Delete heatmap?'
                  description='This will remove the heatmap bundle from the report.'
                  disabled={deletionControlsDisabled}
                  okText='Delete'
                  cancelText='Cancel'
                  okButtonProps={{
                    danger: true,
                    loading: deletingImageKey === selectedHeatmapKey,
                  }}
                  onConfirm={() => handleDeleteScreenshot(selectedScreenshot)}>
                  <Button
                    danger
                    disabled={deletionControlsDisabled}
                    loading={deletingImageKey === selectedHeatmapKey}
                    style={{ marginTop: '0.75rem' }}>
                    Delete Heatmap
                  </Button>
                </Popconfirm>
              </ImageCard>
            )}
          </>
        )}
      </PreviewSection>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    selectedComponents,
    assetScreenshots,
    sectionsByComponent,
    statsBySection,
    statsByComponent,
    componentToNominalRange,
    relevantCDFs,
    selectedAssetModel,
    selectedComponentModels,
  } = useRugReportOps()

  const screenshots = useMemo(() => {
    const shots: SectionScreenshot[] = []

    assetScreenshots?.sectionHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      const componentSections = sectionsByComponent.get(
        screenshot.componentId as ComponentId
      )
      const section = findSectionByKey(componentSections, screenshot.sectionKey)
      const { stats, skipPage } = getRugDataImagePageStats({
        section,
        sectionKey: screenshot.sectionKey,
        sectionable: componentSections,
        componentId: screenshot.componentId as ComponentId,
        statsBySection,
        statsByComponent,
      })
      if (skipPage) return
      const sortOrder = section
        ? (componentSections?.sections.findIndex(
            (candidate) => candidate.name === section.name
          ) ?? -1)
        : -1

      shots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        sectionKey:
          screenshot.sectionKey ?? createReportHeatmapSectionKey(undefined),
        sectionName:
          section?.name ??
          (isFullComponentSectionKey(screenshot.sectionKey)
            ? component.data.name
            : (screenshot.sectionKey ?? component.data.name)),
        sortOrder,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
        stats,
      })
    })

    return sortBy(shots, ['componentId', 'sortOrder', 'sectionName'])
  }, [
    assetScreenshots,
    sectionsByComponent,
    selectedComponents,
    statsBySection,
    statsByComponent,
  ])

  return (
    <>
      {screenshots.map((screenshot, index) => {
        const tracker =
          index === 0
            ? {
                sectionId: RugReportSectionId.RUG_DATA_IMAGES,
                onRender,
              }
            : undefined

        return (
          <ReportPage
            key={`${screenshot.componentId}-${screenshot.sectionKey}`}
            id={index === 0 ? 'rug-data-images' : undefined}
            size='LETTER'
            orientation='landscape'
            pageStyle={pageStyles.imagePage}
            wrap={false}
            contentBottomPadding={SECTION_PDF_FOOTER_PADDING}
            tracker={tracker}
            header={<PageHeader title='RUG Data Images' />}
            footer={
              <PageFooter
                jobNumber={formData.mainInfo.jobNumber}
                revisionNumber={formData.revisionMetadata.revisionNumber}
              />
            }>
            <ImagePageHeader
              componentId={screenshot.componentId as ComponentId}
            />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                marginBottom: 2,
                gap: 16,
              }}>
              <Text
                style={{
                  fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
                  color: REPORT_COLORS.HEADER_TEXT,
                  fontWeight: 'bold',
                }}>
                {screenshot.sectionName}
              </Text>
            </View>

            {(() => {
              const pageStats = screenshot.stats
              const nominalRange = componentToNominalRange?.get(
                screenshot.componentId as ComponentId
              )
              const fallbackNominalThickness =
                nominalRange &&
                GeckoMeasurement.Equivalent(nominalRange.min, nominalRange.max)
                  ? nominalRange.min
                  : undefined
              const sectionNominalThickness =
                pageStats?.nominalThickness ?? fallbackNominalThickness
              const relevantCDF = relevantCDFs?.find(
                (cdf) =>
                  cdf.data.geometry.componentID === screenshot.componentId
              )
              const componentModel = selectedComponentModels?.find(
                (model) => model.id === screenshot.componentId
              )
              const axisLabels = getMeasurementAxisLabels(
                selectedAssetModel?.data.type,
                componentModel?.type
              )
              const comment =
                formData.assetDrawings.images[
                  getSectionHeatmapCommentKey(
                    screenshot.componentId,
                    screenshot.sectionKey
                  )
                ]?.imageCaption

              return (
                <>
                  <View
                    style={{
                      height: SECTION_PDF_HEATMAP_HEIGHT,
                      justifyContent: 'center',
                      marginBottom: 4,
                    }}>
                    <PdfHeatmapWithLegend
                      heatmapSrc={screenshot.signedUrl}
                      legendSrc={screenshot.legendSignedUrl}
                      heatmapHeight={SECTION_PDF_HEATMAP_HEIGHT}
                      legendHeight={SECTION_PDF_HEATMAP_HEIGHT}
                      legendWidth={SECTION_PDF_LEGEND_WIDTH}
                      maxWidth={SECTION_PDF_HEATMAP_MAX_WIDTH}
                      gap={SECTION_PDF_IMAGE_GAP}
                    />
                  </View>
                  {hasSectionData(pageStats) && (
                    <SectionStatsTable
                      sectionStats={pageStats}
                      nominalThickness={sectionNominalThickness}
                      relevantCDF={relevantCDF}
                      comment={comment}
                      axisLabels={axisLabels}
                    />
                  )}
                </>
              )
            })()}
          </ReportPage>
        )
      })}
      <AddendumPages
        sectionId={RugReportSectionId.RUG_DATA_IMAGES}
        sectionTitle='RUG Data Images'
        onRender={screenshots.length === 0 ? onRender : undefined}
      />
    </>
  )
}

export const RugDataImagesSection: ReportSection = {
  id: RugReportSectionId.RUG_DATA_IMAGES,
  title: 'RUG Data Images',
  description: 'Section-by-section thickness visualizations',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
